// src/services/attestationService.js
import api from '../utils/api'; // Your API instance

const ATTESTATION_BASE_URL = '/attestations';

const attestationService = {
  // Get all attestations with pagination, filtering, and search
  getAttestations: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();

      // Add pagination
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);

      // Add search
      if (params.search) queryParams.append('search', params.search);

      // Add filters
      if (params.status) queryParams.append('status', params.status);
      if (params.senderId) queryParams.append('senderId', params.senderId);
      if (params.receiverId) queryParams.append('receiverId', params.receiverId);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);

      // Add sorting
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

      const response = await api.get(`${ATTESTATION_BASE_URL}?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get single attestation by ID
  getAttestationById: async (id) => {
    try {
      const response = await api.get(`${ATTESTATION_BASE_URL}/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Create new attestation
  createAttestation: async (attestationData) => {
    try {
      const response = await api.post(ATTESTATION_BASE_URL, attestationData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update attestation
  updateAttestation: async (id, attestationData) => {
    try {
      const response = await api.put(`${ATTESTATION_BASE_URL}/${id}`, attestationData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Delete attestation
  deleteAttestation: async (id) => {
    try {
      const response = await api.delete(`${ATTESTATION_BASE_URL}/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update attestation status
  updateAttestationStatus: async (id, status) => {
    try {
      const response = await api.patch(`${ATTESTATION_BASE_URL}/${id}/status`, { status });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Sync attestation
  syncAttestation: async (id) => {
    try {
      const response = await api.post(`${ATTESTATION_BASE_URL}/${id}/sync`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Bulk sync attestations
  bulkSyncAttestations: async (ids) => {
    try {
      const response = await api.post(`${ATTESTATION_BASE_URL}/bulk-sync`, { ids });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get attestation statistics
  getAttestationStats: async () => {
    try {
      const response = await api.get(`${ATTESTATION_BASE_URL}/stats`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get attestations by sender agency
  getAttestationsBySender: async (senderId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.status) queryParams.append('status', params.status);

      const response = await api.get(
        `${ATTESTATION_BASE_URL}/by-sender/${senderId}?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get attestations by receiver agency
  getAttestationsByReceiver: async (receiverId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.status) queryParams.append('status', params.status);

      const response = await api.get(
        `${ATTESTATION_BASE_URL}/by-receiver/${receiverId}?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Export attestations
  exportAttestations: async (format = 'csv', filters = {}) => {
    try {
      const response = await api.get(`${ATTESTATION_BASE_URL}/export`, {
        params: { format },
        data: { filters },
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Upload attestation document with metadata
  uploadAttestationDocument: async (attestationId, file, documentData = {}) => {
    try {
      const formData = new FormData();

      // Append the file
      formData.append('file', file);

      // Append all document metadata
      const metadata = {
        name: documentData.name || file.name,
        originalName: file.name,
        fileType: file.type,
        fileExtension: file.name.split('.').pop(),
        fileSize: file.size,
        storageProvider: 'S3', // Specify S3 as storage provider
        altText: documentData.altText || '',
        caption: documentData.caption || '',
        description: documentData.description || '',
        credit: documentData.credit || '',
        copyright: documentData.copyright || '',
        license: documentData.license || '',
        isPublic: documentData.isPublic !== undefined ? documentData.isPublic : true,
        // Optional image dimensions (can be extracted client-side)
        width: documentData.width || null,
        height: documentData.height || null,
        // For videos/audio
        duration: documentData.duration || null,
        // Tags as JSON string
        tags: documentData.tags ? JSON.stringify(documentData.tags) : null,
      };

      // Append each metadata field to FormData
      Object.keys(metadata).forEach(key => {
        if (metadata[key] !== null && metadata[key] !== undefined) {
          formData.append(key, metadata[key]);
        }
      });

      const response = await api.post(
        `${ATTESTATION_BASE_URL}/${attestationId}/documents/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            // You can dispatch an action here to update upload progress
            console.log(`Upload progress: ${percentCompleted}%`);
          },
        }
      );

      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

    // Request presigned URL for upload
  requestPresignedUpload: async (attestationId, fileData) => {
    try {
      const response = await api.post(
        `${ATTESTATION_BASE_URL}/${attestationId}/documents/presigned-upload`,
        {
          fileName: fileData.originalName,
          fileType: fileData.fileType,
          fileSize: fileData.fileSize,
          metadata: {
            name: fileData.name,
            altText: fileData.altText,
            caption: fileData.caption,
            description: fileData.description,
            credit: fileData.credit,
            copyright: fileData.copyright,
            license: fileData.license,
            isPublic: fileData.isPublic,
            tags: fileData.tags,
          }
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Confirm upload completion and save document metadata
  confirmDocumentUpload: async (attestationId, uploadData) => {
    try {
      const response = await api.post(
        `${ATTESTATION_BASE_URL}/${attestationId}/documents/confirm`,
        uploadData
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get attestation documents with pagination
  getAttestationDocuments: async (attestationId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.type) queryParams.append('type', params.type);
      if (params.search) queryParams.append('search', params.search);
      
      const response = await api.get(
        `${ATTESTATION_BASE_URL}/${attestationId}/documents?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get presigned download URL
  getPresignedDownloadUrl: async (attestationId, documentId) => {
    try {
      const response = await api.get(
        `${ATTESTATION_BASE_URL}/${attestationId}/documents/${documentId}/download-url`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Delete document
  deleteAttestationDocument: async (attestationId, documentId) => {
    try {
      const response = await api.delete(
        `${ATTESTATION_BASE_URL}/${attestationId}/documents/${documentId}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get single document
  getDocument: async (attestationId, documentId) => {
    try {
      const response = await api.get(`${ATTESTATION_BASE_URL}/${attestationId}/documents/${documentId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update document metadata
  updateDocumentMetadata: async (attestationId, documentId, metadata) => {
    try {
      const response = await api.patch(
        `${ATTESTATION_BASE_URL}/${attestationId}/documents/${documentId}`,
        metadata
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get document download URL (for S3 signed URLs)
  getDocumentDownloadUrl: async (attestationId, documentId) => {
    try {
      const response = await api.get(
        `${ATTESTATION_BASE_URL}/${attestationId}/documents/${documentId}/download`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};

export default attestationService;